// Considered: UnifiedFilterEditor — it is used as the composer inside this organism (via UnifiedFilterTreeMolecule);
// this organism adds the three-pane shell (pickers rail, variable rail, live-sample) the spec requires.
// See spec Reuse Audit: AdvancedFilterEditorOrganism § "New components — justified".
// Centre pane (EAINT-908 Phase 2): `UnifiedFilterTreeMolecule` mounted bare
// (`framed=false`, `allowGroups`) against the canonical `FilterGroup` root,
// replacing `FilterGroupEditor` — see that molecule's own doc comment, which
// names this organism as its intended host. `FilterGroupEditor` is retired
// from this surface but still backs the legacy simple-mode editor elsewhere
// until Phase 6.
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Trans, useTranslation } from 'localization';
import { isArcLeaf, isPerspectiveLeaf, isPropertyLeaf, isRelationshipLeaf } from '@center-platform/spheres/filters';
import type {
  ArcFilter,
  FilterExpression,
  FilterGroupLeaf,
  PerspectiveFilter,
  RelationshipFilter,
} from 'types/filters';
import { type TagTone } from '../../atoms/Tag/TagAtom';
import FilterVariableRowMolecule from '../../molecules/FilterVariableRow/FilterVariableRowMolecule';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { FilterState, SimpleFilterGroup } from '../../../types/filterState';
import { withStableFilterGroupIds } from '../../../utils/filterGroupIds';
import { seedLeavesIntoGroups, topLevelRowToLeaf } from '../../../utils/filterGroupSeeding';
import { findConflictsByLeaf } from '../../../utils/detectFilterConflict';
import UnifiedFilterTreeMolecule from '../../molecules/UnifiedFilterTree/UnifiedFilterTreeMolecule';
import { nextFilterId } from '../../../utils/ids';
import type { PropertyOption } from '../../molecules/PropertyFilter';
import type { PerspectiveOption } from '../../molecules/PerspectiveFilter';
import type { RelationshipOption } from '../../molecules/RelationshipFilter';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import { FILTER_LANE_COUNT } from '../../../types/filterLane';
import type { FilterLayer } from 'types/contextView';
import type { FilterSaveTarget, FilterSaveTargetAvailability } from '../../../hooks/useLevelFilters';
import FilterSaveTargetMolecule from '../../molecules/FilterSaveTarget/FilterSaveTargetMolecule';
import type { SchemaForFilters } from '../../../utils/filterPropertyBuilder';
import { resolveRailScope, buildRailSections } from '../../../utils/filterRailCatalog';
import {
  perspectiveStateToUnified,
  simpleGroupToUnified,
  unifiedGroupToSimple,
} from '../../../utils/contextFlowSerialization';
import {
  renderArcFilter,
  renderFilterExpression,
  renderPerspectiveFilter,
  renderRelationshipFilter,
} from '../../../utils/renderFilterExpression';
import './AdvancedFilterEditorOrganism.scss';

export interface AdvancedFilterEditorOrganismProps {
  /** The filter state being edited (level filter, segment filter, or an applicability surface). */
  filter: FilterState;
  /** Called when the user clicks Apply. */
  onApply: (filter: FilterState) => void;
  /** Called when the user clicks Back. */
  onBack: () => void;
  /** Breadcrumb label shown in the top bar, e.g. "Level 2 · Priorities". Generic — no context-editor coupling. */
  label: string;
  /** Properties available for filtering (from schema) — the group tree's leaf picker catalog. */
  availableProperties: PropertyOption[];
  /**
   * The unscoped, org-wide property catalog powering the rail. The rail tiers
   * it live against the filter's own type scope, and search reaches across the
   * whole thing so an out-of-scope type's property is still findable. Falls
   * back to `availableProperties` when omitted (hosts with no org-wide catalog
   * simply get a rail that cannot see past their own scope).
   */
  orgWideProperties?: PropertyOption[];
  /**
   * Types the rail falls back to when the filter declares no type/family of its
   * own — the host's scope (for a context level, its eligible schemas).
   */
  scopeTypes?: string[];
  /** Perspectives available for filtering. */
  availablePerspectives: PerspectiveOption[];
  /** Relationships available for filtering (SC4). Omit/empty hides the Relationships lane. */
  availableRelationships?: RelationshipOption[];
  /** Schemas for the Sphere Type/Family chip-selector value editor. */
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  /** Published arc templates for the arc filter rail button + leaf value editor. Omit/empty hides the Arc rail entry. */
  arcTemplates?: ArcTemplateOption[];
  /** Props for PerspectiveSearch (assignee narrowing) — assignee filtering disabled if not provided. */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  readOnly?: boolean;
  /**
   * Which config layer this filter belongs to — shows a tier badge in the header
   * (e.g. CONTEXT, VIEW). Omit for session/new filters.
   */
  provenance?: Extract<FilterLayer, 'context' | 'view' | 'user'>;
  /**
   * When true AND readOnly, shows "Copy to my own filter" CTA — never leaves
   * a read-only user without a forward action (dev note #4).
   */
  canCopy?: boolean;
  /** Fired when the user confirms "Copy to my own filter". */
  onCopyToOwn?: () => void;
  /**
   * Display name of the view this filter is saved in — interpolated into the
   * read-only explanatory note when `provenance === 'view'`. Falls back to a
   * generic "this view" when omitted.
   */
  viewName?: string;
  /**
   * Names the back button destination, e.g. "Priorities" → "← Back to Priorities filter".
   * Falls back to the generic "Back to inspector" when omitted.
   */
  origin?: string;
  /**
   * Save targets for the Apply… button. When provided, Apply opens a 4-target
   * save menu (same as the column filter dropdown). When omitted, Apply calls
   * `onApply` directly (legacy behaviour for non-column surfaces).
   */
  saveTargets?: FilterSaveTargetAvailability[];
  /** Called when the user confirms Apply with a specific save target. */
  onApplyWithTarget?: (filter: FilterState, target: FilterSaveTarget) => void;
  /** System name of the context — for analytics on the save menu. */
  contextSystemName?: string | null;
  /**
   * Renders the "Add condition" picker (the aspect catalog) anchored below the
   * tree's own add-condition button — forwarded to `UnifiedFilterTreeMolecule`
   * untouched. Omitted while the catalog isn't wired to a host's aspect data
   * yet — the button still renders (skeleton). See `AspectCatalogMolecule`'s
   * `datumScope="uf-advanced"` for this dialog's own parity ids.
   */
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
  /**
   * False on a host whose evaluator cannot resolve a nested group — the
   * composer authors a flat list instead and the footer hint explains why
   * (advanced-filters-v2.jsx:1434-1438). Defaults to true (the dialog's own
   * usual "any depth" authoring surface).
   */
  allowGroups?: boolean;
  /**
   * Context-tier constraints that arrive as an immutable prelude — everything
   * authored below is ANDed onto these (advanced-filters-v2.jsx:1364-1401).
   * Hiding them would let the user build an expression that can never match,
   * so they render read-only above the composer whenever non-empty. Omit/empty
   * hides the panel entirely (no context tier above this filter).
   */
  lockedPrelude?: FilterExpression[];
  /**
   * Whether the caller may edit the context that produced `lockedPrelude`.
   * When true, the panel offers "Edit in context"; when false it explains why
   * that's unavailable ("needs context edit rights") instead of hiding the
   * affordance outright.
   */
  canEditContext?: boolean;
  /** Fired when the user clicks "Edit in context" (only rendered when `canEditContext`). */
  onEditInContext?: () => void;
  /**
   * `'wide'` (default) is the full dialog's own chrome. `'narrow'` is the
   * column/popover host (`LevelFilterPopoverOrganism` at column width):
   * forwarded to the composer's `UnifiedFilterTreeMolecule` as its own
   * `layout`, which collapses a nested group's header to the compact
   * all/any chip and moves the joiner above each row
   * (advanced-filters-v2.jsx `inlineCatalog={dense}` — the same axis this
   * dialog's own narrow host uses).
   */
  layout?: 'wide' | 'narrow';
}

// Tones are drawn from TagAtom's canonical palette (no bare hex/rgba) — each
// variable gets a distinct, dark-mode-aware tone instead of the bespoke
// per-token hex values this rail used to hardcode (which also left `@center`
// with no tone at all).
// Each variable's `compatibleTypes` gates the rail button against the
// focused row's property type — `@today` only resolves against a date/time
// value, `@me` only against a person, the three sphere-reference variables
// only against a reference property. Nothing focused leaves every variable
// enabled (inserts a new row, the pre-existing behaviour); a focused row of
// an unlisted type (text, numeric, enum, …) disables every variable, since
// none of them can ever be a valid value there.
const VARIABLE_DEFS = [
  {
    token: '@me' as const,
    labelKey: 'filterEditor.advanced.variables.me',
    // The wireframe gives each variable its own hue family (advanced-filters-v2.jsx:1781-1798):
    // @me cyan, @root blue, @parent violet, @today green. Mapped to the nearest existing
    // TagAtom tone per family — teal for cyan, info for blue (its --info-500 is the
    // wireframe's exact #377FC4), accent for violet, success for green. Until 2026-09-18
    // @root and @parent had each other's family (OQ-19).
    tone: 'teal' as TagTone,
    compatibleTypes: ['person'] as const satisfies readonly PropertyOption['type'][],
    reasonKey: 'filterEditor.advanced.variables.personReason',
  },
  {
    token: '@root' as const,
    labelKey: 'filterEditor.advanced.variables.root',
    tone: 'info' as TagTone,
    compatibleTypes: ['reference'] as const satisfies readonly PropertyOption['type'][],
    reasonKey: 'filterEditor.advanced.variables.referenceReason',
  },
  {
    token: '@parent' as const,
    labelKey: 'filterEditor.advanced.variables.parent',
    tone: 'accent' as TagTone,
    compatibleTypes: ['reference'] as const satisfies readonly PropertyOption['type'][],
    reasonKey: 'filterEditor.advanced.variables.referenceReason',
  },
  {
    token: '@center' as const,
    labelKey: 'filterEditor.advanced.variables.center',
    tone: 'warn' as TagTone,
    compatibleTypes: ['reference'] as const satisfies readonly PropertyOption['type'][],
    reasonKey: 'filterEditor.advanced.variables.referenceReason',
  },
  {
    token: '@today' as const,
    labelKey: 'filterEditor.advanced.variables.today',
    tone: 'success' as TagTone,
    compatibleTypes: ['date', 'datetime'] as const satisfies readonly PropertyOption['type'][],
    reasonKey: 'filterEditor.advanced.variables.dateReason',
  },
] as const;

// Rail rows are icon-keyed by DATA TYPE (design: advanced-filters.jsx, whose
// `EF_TYPES` registry pairs each data type with an icon and a colour). Only the
// icon is adopted here — the design's per-type hex palette has no equivalent in
// our token set and hardcoding one is a design-system violation, so colour comes
// from the standard token pair (accent for the structured lane pseudo-types,
// secondary text for the scalar ones) via the `--lane` modifier.
const PROPERTY_TYPE_ICONS: Record<PropertyOption['type'], string> = {
  text: 'pi pi-align-left',
  numeric: 'pi pi-hashtag',
  enum: 'pi pi-list',
  boolean: 'pi pi-check-square',
  date: 'pi pi-calendar',
  datetime: 'pi pi-calendar',
  urgency: 'pi pi-exclamation-triangle',
  arc: 'pi pi-sitemap',
  relationship: 'pi pi-share-alt',
  perspective: 'pi pi-eye',
  reference: 'pi pi-link',
  // The four unified-filters-v2 additions (EAINT-908 Phase 2) — icons match
  // `filterTypeVisuals.ts`'s `FILTER_TYPE_VISUALS` for the same types.
  multi: 'pi pi-tags',
  person: 'pi pi-user',
  spheretype: 'pi pi-box',
  spherefamily: 'pi pi-th-large',
};

const LANE_PROPERTY_TYPES = new Set<PropertyOption['type']>(['arc', 'relationship', 'perspective']);

const PROVENANCE_BADGE: Partial<Record<string, string>> = {
  context: 'CONTEXT',
  view: 'VIEW',
  user: 'USER',
};

/** Whether any group in the forest holds a condition, at any nesting depth. */
function groupsHaveCondition(groups: SimpleFilterGroup[] | undefined): boolean {
  return (groups ?? []).some((g) => g.filters.length > 0 || groupsHaveCondition(g.groups));
}

/**
 * The words a conflicting locked-prelude clause holds, for the "conflicts with
 * <value>" message — its `values` joined, or its single `value`. `undefined`
 * when the clause carries neither, which reads as the value-less message.
 */
function conflictingClauseValue(clause: FilterExpression): string | undefined {
  if (clause.values?.length) return clause.values.join(', ');
  if (clause.value != null) return String(clause.value);
  return undefined;
}

const AdvancedFilterEditorOrganism: React.FC<AdvancedFilterEditorOrganismProps> = ({
  filter,
  onApply,
  onBack,
  label,
  availableProperties,
  orgWideProperties,
  scopeTypes = [],
  availablePerspectives,
  availableRelationships = [],
  familyTypeSchemas = [],
  arcTemplates = [],
  perspectiveSearchProps,
  readOnly = false,
  provenance,
  canCopy = false,
  onCopyToOwn,
  viewName,
  origin,
  saveTargets,
  onApplyWithTarget,
  contextSystemName,
  renderCatalog,
  allowGroups = true,
  lockedPrelude = [],
  canEditContext = false,
  onEditInContext,
  layout = 'wide',
}) => {
  const { t } = useTranslation();
  const narrowLayout = layout === 'narrow';

  // The composer works purely on groups: fold top-level property filters, plus
  // any top-level arc/relationship/perspective lane (rendered as `{property:
  // 'arc'|'relationship'|'perspective'}` pseudo-rows by `unifiedToFilterState`,
  // or injected directly by a caller reusing this organism outside context),
  // into the group tree — always, not just when the tree was previously empty,
  // so an existing filter is never silently dropped just because the level
  // already had groups (SC7). Top-level channels are AND-combined with the
  // group tree, so they may only be merged into the first group when it is
  // itself an AND group; if the first group is an OR, they get their own
  // leading AND group instead — merging them into the OR would widen
  // `top AND (x OR y)` into `(top OR x OR y)`.
  // `perspectiveFilters` is the legacy `PerspectiveFilterState` editor shape
  // (nested `assigneeFilter`) — converted to the canonical `PerspectiveFilter`
  // pseudo-row value at this boundary, same as `unifiedToFilterState` does for
  // the persisted lane (EAINT-725 Phase 4b — the pinned perspective lane is
  // retired, so this is now the only path perspective reaches the group tree).
  const seedGroups = useCallback((f: FilterState): FilterState => {
    const groups = f.filterGroups ?? [];
    const pf = f.propertyFilters ?? [];
    // Every top-level lane channel folds into the group tree as the CANONICAL
    // leaf it persists as (`{kind, filter}`) — not a pseudo-property row (SC7).
    // Each keeps the identity its payload already carries; an id-less one is
    // normalised once by `withStableFilterGroupIds` below, which mints onto
    // `filter.id` for a lane leaf.
    const arcLeaves: FilterGroupLeaf[] = (f.arcFilters ?? []).map((filter) => ({
      kind: 'arc',
      filter,
    }));
    const relationshipLeaves: FilterGroupLeaf[] = (f.relationshipFilters ?? []).map((filter) => ({
      kind: 'relationship',
      filter,
    }));
    // `PerspectiveFilterState` is the legacy editor shape; convert it to the
    // canonical `PerspectiveFilter` at this boundary, as every other lane
    // channel already stores canonical.
    const perspectiveLeaves: FilterGroupLeaf[] = (f.perspectiveFilters ?? []).map((p) => ({
      kind: 'perspective',
      filter: perspectiveStateToUnified(p),
    }));
    const extraRows: FilterGroupLeaf[] = [
      ...pf.map(topLevelRowToLeaf),
      ...arcLeaves,
      ...relationshipLeaves,
      ...perspectiveLeaves,
    ];
    const seededGroups = seedLeavesIntoGroups(groups, extraRows);
    return {
      mode: f.mode,
      propertyFilters: [],
      perspectiveFilters: [],
      relationshipFilters: [],
      arcFilters: [],
      // Mint ids here, at the state-initialisation boundary, rather than leaving
      // it to FilterGroupEditor's derived `useMemo`. That memo normalises only
      // what it renders; the owning state would keep the id-less seed, so the
      // next rail-add would rebuild the tree from an id-less source and hand
      // every existing row a fresh key — remounting the whole tree and losing
      // in-progress editor state.
      filterGroups: withStableFilterGroupIds(seededGroups),
    };
  }, []);

  const [localFilter, setLocalFilter] = useState<FilterState>(() => seedGroups(filter));
  const [mode, setMode] = useState<'visual' | 'expr'>('visual');
  const [search, setSearch] = useState('');

  // Dirty guard: serialise the initial filter once; compare on Cancel.
  const seedSignatureRef = React.useRef(JSON.stringify(seedGroups(filter)));
  const isDirty = JSON.stringify(localFilter) !== seedSignatureRef.current;

  // Whether this editor opened on an EXISTING expression vs a blank one — decided once,
  // at mount, from the caller's own filter (not the live draft): a filter that starts
  // empty and gets built up is still "New filter expression" throughout that session
  // (advanced-filters-v2.jsx:1046 `isNew ? "New filter expression" : "Edit filter expression"`).
  const [isNew] = useState(() => !groupsHaveCondition(seedGroups(filter).filterGroups));

  // Whether the draft has at least one condition anywhere in the tree — gates Apply and
  // the "add a condition" nudge (advanced-filters-v2.jsx:1070-1074, `!built`).
  const built = useMemo(() => groupsHaveCondition(localFilter.filterGroups), [localFilter]);

  // Apply: either delegate to the 4-target save menu or call onApply directly.
  const handleApply = useCallback(() => onApply(localFilter), [onApply, localFilter]);

  const handleApplyWithTarget = useCallback(
    (target: FilterSaveTarget) => {
      onApplyWithTarget?.(localFilter, target);
    },
    [onApplyWithTarget, localFilter]
  );

  const handleCancel = useCallback(() => {
    if (!isDirty) { onBack(); return; }
    // Simple confirm guard — a full dialog would require ConfirmDialogAtom.
    // eslint-disable-next-line no-alert
    if (window.confirm(t('filterEditor.advanced.cancelGuard', 'You have unsaved changes. Leave without applying?'))) {
      onBack();
    }
  }, [isDirty, onBack, t]);

  const handleReset = useCallback(() => {
    setLocalFilter(seedGroups(filter));
  }, [filter, seedGroups]);

  // ── Group-tree + rail mutators ────────────────────────────────────────────
  // The composer is `UnifiedFilterTreeMolecule` mounted bare (`framed={false}`)
  // against a single canonical `FilterGroup` root — `deriveRoot`/`handleTreeChange`
  // are the only place that root is derived from / written back to the
  // organism's legacy `filterGroups: SimpleFilterGroup[]` state (still the
  // `FilterState` shape every save path expects). Rail "add property / insert
  // variable" affordances always target the ROOT group's own expressions —
  // there is no click-to-activate-a-nested-group affordance in the new tree
  // (FilterGroupNodeMolecule has no such callback); a user who wants a
  // property inside a nested group drags/adds it there directly via that
  // group's own "Add condition" picker.
  const deriveRoot = useCallback((groups: SimpleFilterGroup[]): import('types/filters').FilterGroup => {
    if (groups.length === 0) return { id: nextFilterId(), operator: 'and', expressions: [] };
    if (groups.length === 1) return simpleGroupToUnified(groups[0]);
    return {
      id: nextFilterId(),
      operator: 'and',
      expressions: [],
      groups: groups.map(simpleGroupToUnified),
    };
  }, []);

  const rootGroup = useMemo(
    () => deriveRoot(localFilter.filterGroups ?? []),
    [localFilter.filterGroups, deriveRoot]
  );

  // Per-row problems against the locked context prelude (spec Phase 4: "Per-row
  // problems wired in every host with a locked tier") — every clause the author
  // adds below is ANDed onto `lockedPrelude`, so a row that can never intersect
  // it (provably empty, not a heuristic) is flagged on that row rather than left
  // for the author to discover only after Apply fails silently downstream.
  const preludeProblemsByLeaf = useMemo(
    () =>
      findConflictsByLeaf(
        rootGroup,
        lockedPrelude.map((clause) => ({ clause, tier: 'context' as const }))
      ),
    [rootGroup, lockedPrelude]
  );

  const problemOf = useCallback(
    (leafId: string) => {
      const owner = preludeProblemsByLeaf.get(leafId);
      if (!owner) return undefined;
      const value = conflictingClauseValue(owner.clause);
      const message = value
        ? t('filterEditor.advanced.lockedPreludeConflictWithValue', { value })
        : t('filterEditor.advanced.lockedPreludeConflict');
      return { title: message, message };
    },
    [preludeProblemsByLeaf, t]
  );

  const handleTreeChange = useCallback((next: import('types/filters').FilterGroup) => {
    setLocalFilter((prev) => ({ ...prev, filterGroups: [unifiedGroupToSimple(next)] }));
  }, []);

  // Which group the rail's own additions land in (advanced-filters-v2.jsx
  // `focusId`) — `null` means "no focus, add to the root", matching the
  // prototype's `focusId === "root"` sentinel. Clicking a nested group, or
  // creating one, moves the focus there; the banner above the rail's search
  // reflects it (`focusLabel` in the source).
  const [focusedGroupId, setFocusedGroupId] = useState<string | null>(null);

  // The row currently being worked on — never cleared on blur (see
  // `EditableFilterRowMolecule.onValueFocus`'s own doc comment for why),
  // updated whenever focus lands in a leaf's value editor. Drives which
  // rail variables are offered (`todayOfferedOnlyForDates` etc.) and, when
  // compatible, makes a variable click fill this row instead of adding a
  // new one (`insertVariableFillsFocusedRow`).
  const [focusedLeaf, setFocusedLeaf] = useState<FilterExpression | null>(null);
  const handleValueFocus = useCallback((leaf: FilterExpression) => setFocusedLeaf(leaf), []);

  const groupExists = useCallback(
    (group: import('types/filters').FilterGroup, id: string): boolean =>
      group.id === id || (group.groups ?? []).some((g) => groupExists(g, id)),
    []
  );

  // The tree names the root by its id (a drop onto the root focuses it); here
  // the root is `null`, so it never raises the "adding into a group" banner.
  const focusGroup = useCallback(
    (id: string) => setFocusedGroupId(id === rootGroup.id ? null : id),
    [rootGroup.id]
  );

  // A removed group can leave the focus pointing at nothing — fall back to
  // the root rather than silently dropping every further rail addition.
  useEffect(() => {
    if (focusedGroupId && !groupExists(rootGroup, focusedGroupId)) {
      setFocusedGroupId(null);
    }
  }, [rootGroup, focusedGroupId, groupExists]);

  // Same guard for the focused row — a removed leaf must stop gating the
  // rail's variables against a row that no longer exists.
  useEffect(() => {
    if (!focusedLeaf?.id) return;
    const leafExists = (group: import('types/filters').FilterGroup): boolean =>
      (group.expressions ?? []).some((leaf) => isPropertyLeaf(leaf) && leaf.id === focusedLeaf.id) ||
      (group.groups ?? []).some(leafExists);
    if (!leafExists(rootGroup)) setFocusedLeaf(null);
  }, [rootGroup, focusedLeaf]);

  const insertLeafIntoGroup = useCallback(
    (
      group: import('types/filters').FilterGroup,
      targetGroupId: string | null,
      leaf: import('types/filters').FilterGroupLeaf
    ): import('types/filters').FilterGroup => {
      if (!targetGroupId || group.id === targetGroupId) {
        return { ...group, expressions: [...(group.expressions ?? []), leaf] };
      }
      return {
        ...group,
        groups: (group.groups ?? []).map((g) => insertLeafIntoGroup(g, targetGroupId, leaf)),
      };
    },
    []
  );

  const addLeaf = useCallback(
    (leaf: import('types/filters').FilterGroupLeaf) => {
      setLocalFilter((prev) => {
        const root = deriveRoot(prev.filterGroups ?? []);
        const next = insertLeafIntoGroup(root, focusedGroupId, leaf);
        return { ...prev, filterGroups: [unifiedGroupToSimple(next)] };
      });
    },
    [deriveRoot, insertLeafIntoGroup, focusedGroupId]
  );

  // Fills a single property leaf's value in place, by id, at any depth —
  // the focused-row insertion path (`insertVariableFillsFocusedRow`), as
  // opposed to `addLeaf`'s "always append a new row".
  const updateLeafValueInGroup = useCallback(
    (
      group: import('types/filters').FilterGroup,
      leafId: string,
      value: string
    ): import('types/filters').FilterGroup => ({
      ...group,
      expressions: (group.expressions ?? []).map((leaf) =>
        isPropertyLeaf(leaf) && leaf.id === leafId ? { ...leaf, value } : leaf
      ),
      groups: (group.groups ?? []).map((g) => updateLeafValueInGroup(g, leafId, value)),
    }),
    []
  );

  // The type of whatever's in `availableProperties`/`orgWideProperties` for
  // the focused row's property — `undefined` when nothing is focused (every
  // variable stays offered) or the property isn't found (treated the same
  // as incompatible, since compatibility can't be confirmed).
  const focusedLeafType = useMemo(() => {
    if (!focusedLeaf) return undefined;
    const catalog = orgWideProperties ?? availableProperties;
    return catalog.find((p) => p.key === focusedLeaf.property)?.type;
  }, [focusedLeaf, orgWideProperties, availableProperties]);

  const handleInsertVariable = useCallback(
    (token: string, compatibleTypes: readonly PropertyOption['type'][]) => {
      if (focusedLeaf?.id && focusedLeafType && compatibleTypes.includes(focusedLeafType)) {
        setLocalFilter((prev) => {
          const root = deriveRoot(prev.filterGroups ?? []);
          const next = updateLeafValueInGroup(root, focusedLeaf.id as string, token);
          return { ...prev, filterGroups: [unifiedGroupToSimple(next)] };
        });
        return;
      }
      addLeaf({ id: nextFilterId(), property: '', operator: 'equals', value: token } as FilterExpression);
    },
    [addLeaf, deriveRoot, focusedLeaf, focusedLeafType, updateLeafValueInGroup]
  );

  const handleAddProperty = useCallback(
    (propertyKey: string, aspectSystemName?: string) => {
      addLeaf({
        id: nextFilterId(),
        property: propertyKey,
        aspect: aspectSystemName,
        operator: 'equals',
        value: '',
      } as FilterExpression);
    },
    [addLeaf]
  );

  const query = search.trim().toLowerCase();
  const railSchemas = familyTypeSchemas as unknown as SchemaForFilters[];

  // The scope is read from the filter being edited — including the group tree,
  // which is where the advanced editor puts everything — so adding a Sphere
  // Type row re-tiers the rail immediately, before Apply.
  const scope = useMemo(
    () => resolveRailScope(localFilter, railSchemas, scopeTypes),
    [localFilter, railSchemas, scopeTypes]
  );

  const {
    sections: railSections,
    otherTypesCount,
    effectiveTypes: railScopeTypes,
  } = useMemo(
    () =>
      buildRailSections({
        properties: orgWideProperties ?? availableProperties,
        scope,
        query,
        schemas: railSchemas,
      }),
    [orgWideProperties, availableProperties, scope, query, railSchemas]
  );

  const hasRailMatches = railSections.some((s) => s.items.length > 0);

  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // A lane leaf carries its whole payload on `.filter`, so the preview has to
  // narrow the canonical union and dispatch to the per-lane renderers — the
  // generic property line would print `[object Object]`. Now that every lane
  // composes through the group tree (EAINT-725), this is the preview's main
  // content, not an edge case.
  const renderLeaf = useCallback(
    (leaf: FilterGroupLeaf): string => {
      if (isArcLeaf(leaf)) {
        const arc = leaf.filter;
        return renderArcFilter(arc ?? { operator: 'in' }, {
          arcLabel:
            arcTemplates.find((a) => a.systemName === arc?.arcSelector)?.name ??
            t('filters.lane.arc.badge'),
          labels: {
            none: t('context.editor.arcFilters.noArcMatches'),
            exists: t('context.editor.arcFilters.existenceCheck'),
          },
        });
      }
      if (isRelationshipLeaf(leaf)) {
        const rel = leaf.filter;
        return renderRelationshipFilter(
          rel ?? { relationshipSystemName: '', operator: 'in', relatedTo: [] },
          availableRelationships.find((r) => r.systemName === rel?.relationshipSystemName)?.label ||
            rel?.relationshipSystemName ||
            t('filters.lane.relationship.badge')
        );
      }
      if (isPerspectiveLeaf(leaf)) {
        const per = leaf.filter;
        return renderPerspectiveFilter(
          per ?? { perspectiveSystemName: '', operator: 'in', assignees: [] },
          availablePerspectives.find((o) => o.systemName === per?.perspectiveSystemName)?.label ||
            per?.perspectiveSystemName ||
            t('filters.lane.perspective.badge')
        );
      }
      return `${leaf.property || '_'} ${String(leaf.operator)} ${String(leaf.value ?? '_')}`;
    },
    [arcTemplates, availableRelationships, availablePerspectives, t]
  );

  // `seedGroups` folds every top-level channel (property + all three lanes)
  // into the group tree, so the tree is the whole filter — the preview reads
  // only from it.
  const expressionText = useMemo(() => {
    const lines: string[] = [];
    const renderGroup = (g: SimpleFilterGroup, indent: string) => {
      lines.push(`${indent}${g.operator.toUpperCase()}`);
      g.filters.forEach((f) => lines.push(`${indent}  ${renderLeaf(f)}`));
      (g.groups ?? []).forEach((child) => renderGroup(child, `${indent}  `));
    };
    (localFilter.filterGroups ?? []).forEach((g, gi) => {
      if (gi > 0) lines.push('AND');
      renderGroup(g, '');
    });
    return lines.join('\n') || t('filterEditor.advanced.expressionEmpty');
  }, [localFilter, renderLeaf, t]);

  let titleText: string;
  if (readOnly) titleText = t('filterEditor.advanced.titleView');
  else if (isNew) titleText = t('filterEditor.advanced.titleNew');
  else titleText = t('filterEditor.advanced.title');

  const backLabel = origin
    ? t('filterEditor.advanced.backToOrigin', { origin })
    : t('filterEditor.advanced.backToInspector');

  // The top bar's trailing actions: a read-only surface offers Close and (when
  // allowed) Copy to my own; an editable one offers Reset, Cancel and Apply.
  const renderTopbarActions = (): React.ReactNode => {
    if (readOnly) {
      return (
        <>
          {/* Explanatory note — states WHY this surface is read-only, tier-specific. */}
          <span className="advanced-filter-editor-organism__readonly-note">
            {provenance === 'view'
              ? t('filterEditor.advanced.readOnlyNoteView', { viewName: viewName ?? t('filterEditor.advanced.readOnlyNoteViewFallback') })
              : t('filterEditor.advanced.readOnlyNoteContext')}
          </span>
          <button
            type="button"
            className="advanced-filter-editor-organism__action-btn"
            onClick={onBack}
            data-testid="advanced-filter-close"
            data-datum="uf-advanced.close"
          >
            <i className="pi pi-arrow-left" aria-hidden="true" />
            {t('filterEditor.advanced.close')}
          </button>
          {/* Copy to my own is the only forward action available on a read-only surface */}
          {canCopy && onCopyToOwn && (
            <button
              type="button"
              className="advanced-filter-editor-organism__action-btn advanced-filter-editor-organism__action-btn--primary"
              onClick={onCopyToOwn}
              data-testid="advanced-filter-copy-own"
              data-datum="uf-advanced.copy-to-my-own-filter"
            >
              <i className="pi pi-copy" aria-hidden="true" />
              {t('filterEditor.advanced.copyToMyOwn')}
            </button>
          )}
        </>
      );
    }
    return (
      <>
        {/* Nudges toward the one thing that unblocks Apply — an unbuilt expression
            can't be saved (advanced-filters-v2.jsx:1070-1074, `!built`). */}
        {!built && (
          <span className="advanced-filter-editor-organism__unbuilt-note">
            {t('filterEditor.advanced.unbuiltNote')}
          </span>
        )}
        <button
          type="button"
          className="advanced-filter-editor-organism__action-btn"
          onClick={handleReset}
          data-testid="advanced-filter-reset"
          data-datum="uf-advanced.reset"
        >
          {t('filterEditor.advanced.reset')}
        </button>
        <button
          type="button"
          className="advanced-filter-editor-organism__action-btn"
          onClick={handleCancel}
          data-testid="advanced-filter-cancel"
          data-datum="uf-advanced.cancel"
        >
          {t('filterEditor.advanced.cancel')}
        </button>
        {/* Apply: 4-target save menu when saveTargets provided, direct apply otherwise.
            Disabled while `!built` — mirrors the prototype's `disabled={!built}` on its
            own Apply… button (advanced-filters-v2.jsx:1092). */}
        {saveTargets && onApplyWithTarget ? (
          <FilterSaveTargetMolecule
            targets={saveTargets}
            onSelect={handleApplyWithTarget}
            contextSystemName={contextSystemName}
            disabled={!built}
            className="advanced-filter-editor-organism__apply-menu"
            triggerDatumId="uf-advanced.apply"
            datumScope="uf-advanced"
          />
        ) : (
          <button
            type="button"
            className="advanced-filter-editor-organism__action-btn advanced-filter-editor-organism__action-btn--primary"
            onClick={handleApply}
            disabled={!built}
            data-testid="advanced-filter-apply"
            data-datum="uf-advanced.apply"
          >
            {t('filterEditor.advanced.apply')}
          </button>
        )}
      </>
    );
  };

  // One rail section: its heading (core, or a type with its avatar and an
  // in-scope count / out-of-scope badge) and its pickable property rows.
  const renderRailSection = (section: (typeof railSections)[number]): React.ReactNode => {
    let trailing: React.ReactNode = null;
    if (!section.inScope) {
      trailing = (
        <span className="advanced-filter-editor-organism__section-badge">
          {t('filterEditor.advanced.notInScope')}
        </span>
      );
    } else if (section.key !== 'core') {
      trailing = <span className="advanced-filter-editor-organism__section-count">{section.items.length}</span>;
    }
    return (
      <React.Fragment key={section.key}>
        <div
          // One section per type in scope — the prototype's `each(typeIds)` /
          // `each(afScope.types)` collection, which PAR-5 requires the render to declare.
          data-container="each(typeIds)"
          className={`advanced-filter-editor-organism__section-label${
            section.inScope ? '' : ' advanced-filter-editor-organism__section-label--out-of-scope'
          }`}
          data-testid={`advanced-filter-rail-section-${section.key}`}
        >
          {section.key === 'core' ? (
            <span
              data-datum={query ? 'uf-advanced.core' : 'uf-advanced.core#2'}
              data-kind="heading"
            >
              {t('filterEditor.advanced.coreSection')}
            </span>
          ) : (
            <span
              className={`advanced-filter-editor-organism__section-type${
                query ? '' : ' advanced-filter-editor-organism__section-type--heading'
              }`}
              data-datum={query ? 'uf-advanced.eftypename-t' : 'uf-advanced.heading'}
              data-kind="heading"
            >
              {section.avatar && (
                <img
                  src={section.avatar}
                  alt=""
                  aria-hidden="true"
                  className="advanced-filter-editor-organism__section-avatar"
                />
              )}
              {section.label}
            </span>
          )}
          <span className="advanced-filter-editor-organism__section-spacer" />
          {trailing}
        </div>
        {section.items.map((p) => (
          <button
            key={`${section.key}:${p.key}`}
            type="button"
            className={`advanced-filter-editor-organism__picker-row${
              section.inScope ? '' : ' advanced-filter-editor-organism__picker-row--dim'
            }`}
            onClick={() => handleAddProperty(p.key, p.aspectSystemName)}
            disabled={readOnly}
            data-testid={`property-add-${p.key}`}
          >
            <i
              className={`${PROPERTY_TYPE_ICONS[p.type] ?? 'pi pi-tag'}${
                LANE_PROPERTY_TYPES.has(p.type)
                  ? ' advanced-filter-editor-organism__picker-icon--lane'
                  : ''
              }`}
              aria-hidden="true"
            />
            <span className="advanced-filter-editor-organism__picker-label">{p.label}</span>
            {p.hasVariants && (
              <span
                className="advanced-filter-editor-organism__variant-badge"
                title={t('filterEditor.advanced.variantHint')}
              >
                {t('filterEditor.advanced.variant')}
              </span>
            )}
            <i
              className="pi pi-plus advanced-filter-editor-organism__add-icon"
              aria-hidden="true"
            />
          </button>
        ))}
      </React.Fragment>
    );
  };

  // The rail's variables block, filtered by the same search as the properties.
  const renderVariables = (): React.ReactNode => {
    const variables = VARIABLE_DEFS.filter(
      (v) => !query || t(v.labelKey).toLowerCase().includes(query) || v.token.includes(query)
    );
    if (variables.length === 0) return null;
    return (
      <>
        <div className="advanced-filter-editor-organism__section-label">
          {t('filterEditor.advanced.variablesLabel')}
          <span className="advanced-filter-editor-organism__variables-hint">
            {t('filterEditor.advanced.variablesHint')}
          </span>
        </div>
        {variables.map((v) => {
          const disabled =
            !!focusedLeafType &&
            !(v.compatibleTypes as readonly PropertyOption['type'][]).includes(focusedLeafType);
          return (
            <FilterVariableRowMolecule
              key={v.token}
              token={v.token}
              label={t(v.labelKey)}
              tone={v.tone}
              onInsert={(token) => handleInsertVariable(token, v.compatibleTypes)}
              disabled={disabled}
              reason={disabled ? t(v.reasonKey) : undefined}
              data-container="each(variables)"
              data-datum="uf-advanced.button#2"
              data-datum-value="uf-advanced.v-v"
            />
          );
        })}
      </>
    );
  };

  // Left rail — search, the focus banner, the scope banners, then the
  // properties / perspectives / variables sections.
  const renderRail = (): React.ReactNode => (
    <div className="advanced-filter-editor-organism__rail advanced-filter-editor-organism__rail--left">
      <div className="advanced-filter-editor-organism__rail-search">
        <div className="advanced-filter-editor-organism__search-wrapper">
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('filterEditor.advanced.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="advanced-filter-editor-organism__search-input"
            aria-label={t('filterEditor.advanced.searchPlaceholder')}
            data-testid="advanced-filter-rail-search"
            data-datum="uf-advanced.search-properties-perspectives"
          />
          <i className="pi pi-search advanced-filter-editor-organism__search-icon" aria-hidden="true" />
        </div>
      </div>
      {/* Which group the rail's picks land in — only shown once a nested
          group is focused; "top level" resets to the root. */}
      {focusedGroupId && (
        <div
          className="advanced-filter-editor-organism__focus-banner"
          data-testid="advanced-filter-focus-banner"
        >
          <i className="pi pi-arrow-down-right" aria-hidden="true" />
          <span>
            <Trans
              i18nKey="filterEditor.advanced.addingIntoFocusedGroup"
              components={{ bold: <strong data-datum="uf-advanced.the-focused-group" data-kind="strong" /> }}
            />
          </span>
          <button
            type="button"
            className="advanced-filter-editor-organism__focus-reset"
            onClick={() => setFocusedGroupId(null)}
            data-datum="uf-advanced.top-level"
            data-kind="button"
          >
            {t('filterEditor.advanced.focusTopLevel')}
          </button>
        </div>
      )}
      <div className="advanced-filter-editor-organism__rail-scroll">
        {/* Scope banner (browse mode only) — states which types the rail is
            showing and where that scope came from. */}
        {!query && railScopeTypes.length > 0 && (
          <div
            className="advanced-filter-editor-organism__scope-banner"
            data-testid="advanced-filter-scope-banner"
          >
            <i
              className={scope.source === 'filter' ? 'pi pi-filter' : 'pi pi-sitemap'}
              aria-hidden="true"
            />
            <span>
              <Trans
                i18nKey={
                  scope.source === 'filter'
                    ? 'filterEditor.advanced.scopedFromFilter'
                    : 'filterEditor.advanced.scopedFromLevel'
                }
                count={railScopeTypes.length}
                components={{ bold: <strong data-datum="uf-advanced.type" data-kind="strong" /> }}
              />
            </span>
          </div>
        )}

        {/* Search escapes the scope on purpose — say so, so an out-of-scope
            hit is not read as a bug. */}
        {query && (
          <div
            className="advanced-filter-editor-organism__search-scope-note"
            data-testid="advanced-filter-search-scope-note"
          >
            <Trans
              i18nKey="filterEditor.advanced.searchingAllTypes"
              count={railSchemas.filter((s) => !s.is_abstract && !s.is_hidden).length}
              components={{ bold: <strong data-datum="uf-advanced.ef-org-type-count" data-kind="strong" /> }}
            />
          </div>
        )}

        {railSections.map(renderRailSection)}

        {query && !hasRailMatches && (
          <div
            className="advanced-filter-editor-organism__rail-empty"
            data-testid="advanced-filter-rail-empty"
          >
            {t('filterEditor.advanced.noPropertiesMatch')}
          </div>
        )}

        {/* Browse mode ends with the escape hatch into the full catalog. */}
        {!query && otherTypesCount > 0 && (
          <button
            type="button"
            className="advanced-filter-editor-organism__search-more"
            onClick={() => searchInputRef.current?.focus()}
            data-testid="advanced-filter-search-more"
            data-datum="uf-advanced.search"
          >
            <i className="pi pi-search" aria-hidden="true" />
            <span className="advanced-filter-editor-organism__search-more-label">
              {t('filterEditor.advanced.searchMoreTypes', { count: otherTypesCount })}
            </span>
          </button>
        )}

        {renderVariables()}
      </div>
    </div>
  );

  // Context-tier constraints arrive as an immutable prelude. Hiding them
  // would let the user build an expression that can never match
  // (advanced-filters-v2.jsx:1364-1401).
  const renderPrelude = (): React.ReactNode => {
    if (lockedPrelude.length === 0) return null;
    return (
      <div className="advanced-filter-editor-organism__prelude">
        <div className="advanced-filter-editor-organism__prelude-header">
          <i className="pi pi-lock" aria-hidden="true" />
          <span>{t('filterEditor.advanced.lockedPreludeTitle')}</span>
          <span className="advanced-filter-editor-organism__prelude-spacer" />
          {canEditContext ? (
            <button
              type="button"
              className="advanced-filter-editor-organism__prelude-edit-btn"
              onClick={onEditInContext}
              data-testid="advanced-filter-edit-in-context"
              data-datum="uf-advanced.edit-in-context"
            >
              <i className="pi pi-external-link" aria-hidden="true" />
              {t('filterEditor.advanced.editInContext')}
            </button>
          ) : (
            <span className="advanced-filter-editor-organism__prelude-locked-note">
              {t('filterEditor.advanced.needsContextEditRights')}
            </span>
          )}
        </div>
        <div className="advanced-filter-editor-organism__prelude-body">
          {lockedPrelude.map((f) => (
            <div key={f.id} className="advanced-filter-editor-organism__prelude-row">
              <i className="pi pi-arrow-down-right" aria-hidden="true" />
              {renderFilterExpression(f)}
            </div>
          ))}
        </div>
        <div className="advanced-filter-editor-organism__prelude-footer">
          {t('filterEditor.advanced.lockedPreludeFooter')}
        </div>
      </div>
    );
  };

  // The composer's body: the visual tree with its hint, or the read-only
  // expression view.
  const renderComposerBody = (): React.ReactNode => {
    if (mode !== 'visual') {
      // Expression view — read-only per spec
      return (
        <div
          className="advanced-filter-editor-organism__expression"
          data-testid="expression-view"
        >
          <pre className="advanced-filter-editor-organism__expression-code">
            {expressionText}
          </pre>
        </div>
      );
    }
    return (
      <div className="advanced-filter-editor-organism__visual">
        {/* Recursive AND/OR property-group tree (nested groups, any depth) —
            the canonical `UnifiedFilterTreeMolecule` mounted bare
            (`framed={false}`): a perspective/relationship/arc leaf is an
            ordinary `FilterGroupLeaf`, never re-wrapped, and lives here
            the same as any property leaf — added via the rail or a
            group's own "Add condition" picker (there is no pinned-lane
            pattern left; EAINT-725 Phase 4b retired the last one,
            perspective). Read-only mode disables interaction via the
            wrapping `composer--readonly` pointer-events guard above, not
            a prop on the tree itself. */}
        <UnifiedFilterTreeMolecule
          value={rootGroup}
          onChange={readOnly ? () => undefined : handleTreeChange}
          problemOf={problemOf}
          framed={false}
          allowGroups={allowGroups}
          availableProperties={availableProperties}
          arcTemplates={arcTemplates}
          availableRelationships={availableRelationships}
          familyTypeSchemas={familyTypeSchemas}
          availablePerspectives={availablePerspectives}
          perspectiveSearchProps={perspectiveSearchProps}
          renderCatalog={renderCatalog}
          datumScope="uf-advanced"
          rootTabsDatumId="uf-advanced.tabs#2"
          focusedGroupId={focusedGroupId ?? undefined}
          onFocusGroup={readOnly ? undefined : focusGroup}
          onValueFocus={readOnly ? undefined : handleValueFocus}
          layout={layout}
          // This editor is where deeper nesting goes: a narrow layout here
          // keeps "Add group" at every depth instead of the "use Advanced" cap.
          capNarrowNesting={false}
          nestedToggleDatumId={narrowLayout ? 'uf-advanced.any' : undefined}
          nestedJoinerLabelDatumId={narrowLayout ? 'uf-advanced.label' : 'uf-advanced.label#2'}
          nestedJoinerControlDatumId={narrowLayout ? 'uf-advanced.or' : 'uf-advanced.or#2'}
        />

        <div className="advanced-filter-editor-organism__hint">
          <i className="pi pi-info-circle" aria-hidden="true" />
          <span>
            {allowGroups ? (
              <Trans
                i18nKey="filterEditor.advanced.groupHint"
                // The count comes from the lane map, so the hint cannot fall behind
                // the lanes the catalog below it actually offers.
                values={{ laneCount: FILTER_LANE_COUNT }}
                components={{
                  addFilter: <strong data-datum="uf-advanced.add-filter" />,
                  addGroup: <strong data-datum="uf-advanced.add-group#2" />,
                  any: <strong data-datum="uf-advanced.any" />,
                }}
              />
            ) : (
              <Trans
                i18nKey="filterEditor.advanced.flatListHint"
                components={{ any: <strong data-datum="uf-advanced.any#2" /> }}
              />
            )}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      className="advanced-filter-editor-organism"
      data-testid="advanced-filter-editor"
      data-container="ui-advanced"
    >
      {/* ── Top bar ── */}
      <div className="advanced-filter-editor-organism__topbar">
        <button
          type="button"
          className="advanced-filter-editor-organism__back-btn"
          onClick={readOnly ? onBack : handleCancel}
          aria-label={backLabel}
          data-datum="uf-advanced.back-to-inspector"
        >
          <i className="pi pi-arrow-left" aria-hidden="true" />
          {backLabel}
        </button>
        <span className="advanced-filter-editor-organism__sep" aria-hidden="true" />

        {/* Tier badge — CONTEXT / VIEW / USER */}
        {provenance && (
          <span
            className={`advanced-filter-editor-organism__tier-badge advanced-filter-editor-organism__tier-badge--${provenance}`}
            data-datum="uf-advanced.pill"
            data-kind="pill"
          >
            {PROVENANCE_BADGE[provenance]}
          </span>
        )}

        {/* READ ONLY badge */}
        {readOnly && (
          <span className="advanced-filter-editor-organism__readonly-badge">
            {t('filterEditor.advanced.readOnly')}
          </span>
        )}

        <div className="advanced-filter-editor-organism__breadcrumb">
          <span
            className="advanced-filter-editor-organism__breadcrumb-eyebrow"
            data-datum="uf-advanced.level-2-priorities"
            data-kind="label"
          >
            {label}
          </span>
          <span className="advanced-filter-editor-organism__breadcrumb-title">{titleText}</span>
        </div>
        <span className="advanced-filter-editor-organism__spacer" />
        <fieldset
          className="advanced-filter-editor-organism__mode-toggle"
          data-datum="uf-advanced.tabs"
          data-kind="tabs"
        >
          <legend className="advanced-filter-editor-organism__visually-hidden">
            {t('filterEditor.advanced.modeToggleLabel')}
          </legend>
          <button
            type="button"
            className={`advanced-filter-editor-organism__mode-btn${mode === 'visual' ? ' advanced-filter-editor-organism__mode-btn--active' : ''}`}
            onClick={() => setMode('visual')}
            aria-pressed={mode === 'visual'}
            data-testid="mode-visual"
          >
            <i className="pi pi-palette advanced-filter-editor-organism__mode-btn-icon" aria-hidden="true" />
            {t('filterEditor.advanced.modeVisual')}
          </button>
          <button
            type="button"
            className={`advanced-filter-editor-organism__mode-btn${mode === 'expr' ? ' advanced-filter-editor-organism__mode-btn--active' : ''}`}
            onClick={() => setMode('expr')}
            aria-pressed={mode === 'expr'}
            data-testid="mode-expression"
          >
            <i className="pi pi-code advanced-filter-editor-organism__mode-btn-icon" aria-hidden="true" />
            {t('filterEditor.advanced.modeExpression')}
          </button>
        </fieldset>
        <span className="advanced-filter-editor-organism__sep" aria-hidden="true" />

        {renderTopbarActions()}
      </div>

      {/* ── Three-pane body ── */}
      <div className="advanced-filter-editor-organism__body">
        {/* Left rail — properties / perspectives / variables */}
        {renderRail()}

        {/* Center — composer (72% opacity + pointer-events:none in read-only mode per dev note) */}
        <div className={`advanced-filter-editor-organism__composer${readOnly ? ' advanced-filter-editor-organism__composer--readonly' : ''}`}>
          {renderPrelude()}
          {/* Read-only banner above the tree — states WHY interaction is disabled and
              names the forward action, distinct from the header's own note + button
              (advanced-filters-v2.jsx:1408-1413). */}
          {readOnly && (
            <div className="advanced-filter-editor-organism__viewing-note">
              <i className="pi pi-eye" aria-hidden="true" />
              <span>
                <Trans
                  i18nKey="filterEditor.advanced.viewingNote"
                  components={{
                    bold: (
                      <strong
                        data-datum="uf-advanced.copy-to-my-own-filter#2"
                        data-kind="strong"
                      />
                    ),
                  }}
                />
              </span>
            </div>
          )}
          {renderComposerBody()}
        </div>

        {/* Right rail — live sample (static for now; live execution is a follow-up) */}
        <div className="advanced-filter-editor-organism__rail advanced-filter-editor-organism__rail--right">
          <div className="advanced-filter-editor-organism__sample-header">
            <span
              className="advanced-filter-editor-organism__live-dot"
              aria-hidden="true"
              data-datum="uf-advanced.status-mark"
              data-kind="status-mark"
            />
            <span className="advanced-filter-editor-organism__sample-title">
              {t('filterEditor.advanced.sampleTitle')}
            </span>
          </div>
          <div className="advanced-filter-editor-organism__sample-empty">
            <i className="pi pi-search" aria-hidden="true" />
            <span>{t('filterEditor.advanced.sampleEmpty')}</span>
          </div>
          <div className="advanced-filter-editor-organism__sample-footer">
            <i className="pi pi-bolt" aria-hidden="true" />
            {t('filterEditor.advanced.sampleLiveNote')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilterEditorOrganism;
